import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Flex from 'components/ui/Flex';
import SearchInput from 'components/Search/SearchInput';
import OrderResultsTable from 'components/Returns/OrderResultsTable';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import useDebounce from 'hooks/useDebounce';
import {
    orderSelectors,
    orderThunks,
    selectOrderSearchFilters,
    selectedOrderSet,
    setOrderSearchFilters,
} from 'store/reducers/orderSlice';
import { clearReturnSlip } from 'store/reducers/returnSlice';
import { setOrigin } from 'store/reducers/navigationSlice';
import { ReturnOrderHeader } from 'returnCenter-api';
import SearchResults from './SearchResults';

type ArticleSearchStepProps = {
    customerCode: string;
};

const ArticleSearchStep = ({ customerCode }: ArticleSearchStepProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [query, setQuery] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const results = useAppSelector(orderSelectors.selectOrderHeaders);
    const loading = useAppSelector(orderSelectors.selectSearchLoading);
    const error = useAppSelector(orderSelectors.selectErrorMessage);
    const { from, to } = useAppSelector(selectOrderSearchFilters);

    const trimmed = query.trim();

    const handleSearch = useCallback(() => {
        if (!trimmed) return;

        dispatch(setOrderSearchFilters({ query: trimmed }));

        return dispatch(
            orderThunks.searchOrders({
                searchString: trimmed,
                customerCode,
                searchArticles: true,
                from: from?.toISOString(),
                to: to?.toISOString(),
            }),
        );
    }, [dispatch, trimmed, customerCode, from, to]);

    const debouncedSearch = useDebounce(handleSearch, 350);

    useEffect(() => {
        if (trimmed.length >= 3) debouncedSearch();
    }, [trimmed, from, to]);

    const matchesSelectedCustomer = useCallback(
        (order: ReturnOrderHeader) => {
            const selectedCustomerCode = customerCode.trim().toLowerCase();
            const orderCustomerCode = order.customerCode?.trim().toLowerCase();
            return !!orderCustomerCode && orderCustomerCode === selectedCustomerCode;
        },
        [customerCode],
    );

    const handleOrderSelect = useCallback(
        (order: ReturnOrderHeader) => {
            if (!order.orderNumber) return;
            if (!matchesSelectedCustomer(order)) return;

            dispatch(clearReturnSlip());
            dispatch(selectedOrderSet(order));
            dispatch(setOrigin('customer'));

            navigate(`/search/order/${order.orderNumber}`, {
                state: { orderIdFromPath: order.orderNumber },
            });
        },
        [dispatch, matchesSelectedCustomer, navigate],
    );

    const handleClear = useCallback(() => {
        setQuery('');
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.focus();
        }
    }, []);

    return (
        <StepContent column $grow={1}>
            <SearchRow gap="0.5rem" align="center">
                <SearchInput
                    inputRef={inputRef}
                    containerRef={containerRef}
                    placeholder={t('searchByArticlePlaceholder')}
                    searchString={query}
                    onSearch={(e) => setQuery(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearch();
                        }
                    }}
                    onClear={handleClear}
                    onIconClick={handleSearch}
                />
            </SearchRow>
            <Flex gap="1rem" column>
                <SearchResults loading={loading} error={error} hasResults={(results ?? []).length > 0}>
                    <OrderResultsTable
                        orders={results ?? []}
                        onOrderSelect={handleOrderSelect}
                        isOrderSelectable={matchesSelectedCustomer}
                    />
                </SearchResults>
            </Flex>
        </StepContent>
    );
};

export default ArticleSearchStep;

const StepContent = styled(Flex)`
    margin-top: 0.5rem;
`;

const SearchRow = styled(Flex)`
    width: 100%;
`;
